import { IAMCRequest } from '../utils/entities/interface';
import { Pagination } from './booking';
import { get, post } from './https';

interface AmcRequestsResponse {
  status: boolean;
  data: IAMCRequest[];
  count: number;
  pagination: Pagination;
}

const GETAmcRequests = async ({
  page = 1,
  limit = 25,
  searchQuery = '',
  sortField = '',
  sortOrder = 'DESC',
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<AmcRequestsResponse> => {
  // Initialize the base URL
  let url = `/api/v1/admin/lead-list?limit=${limit}&page=${page}`;

  // Conditionally add search parameter if provided
  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  // Conditionally add sortField and sortOrder if they are provided
  if (sortField && sortField !== 'name') {
    // assuming 'name' is the default field, you can change it if needed
    url += `&sortField=${sortField}`;
  }

  if (sortOrder && sortOrder !== 'ASC') {
    // assuming 'ASC' is the default sort order
    url += `&sortOrder=${sortOrder}`;
  }

  // Fetch the response
  const response = await get(url); // Use the previously defined `get` function

  if (response.status == 200) {
    return response.data; // If status is true, return the user data
  } else {
    throw new Error('Failed to fetch users');
  }
};


// Admin: Get a single AMC request by lead id
const GETAmcRequestById = async (leadId: string) => {
  if (!leadId) {
    throw new Error('Lead id is required');
  }
  const response = await get(`/api/v1/admin/lead/${leadId}`);
  if (response?.status === 200 && response?.data?.status) {
    return response.data.data;
  }
  throw new Error(response?.data?.message || 'Failed to fetch AMC request');
};

// Admin: update enquiry status (e.g. reject an AMC request)
// NOTE: the backend looks up by the human-readable `enquiryId` code, not the Mongo _id
const UpdateEnquiryStatus = async (
  enquiryId: string,
  status: string,
  reason?: string,
) => {
  if (!enquiryId) {
    throw new Error('Enquiry id is required');
  }
  const response = await post('/api/v1/admin/enquiry/status/manage', {
    enquiryId,
    status,
    ...(reason ? { reason } : {}),
  });
  if (response?.status === 200 && response?.data?.status) {
    return response.data;
  }
  throw new Error(
    response?.data?.message || 'Failed to update enquiry status',
  );
};

// Admin: list quotations submitted for a request (AMC / Copper Piping /
// Free Consultancy / Sell Old AC all share the same Quote records)
const GETQuotesForEnquiry = async (enquiryId: string) => {
  if (!enquiryId) return [];
  const response = await get(`/api/v1/admin/enquiry/${enquiryId}/quotes`);
  if (response?.status === 200 && response?.data?.status) {
    return response.data.data || [];
  }
  return [];
};

// Admin: create an AMC lead on a customer's behalf.
const AdminCreateAmcLead = async (payload: Record<string, any>) => {
  const response = await post('/api/v1/admin/lead/create', payload);
  if (response.status === 200 || response.status === 201) {
    return response.data;
  }
  throw new Error(response?.data?.message || 'Failed to create request');
};

export {
  GETAmcRequests,
  GETAmcRequestById,
  UpdateEnquiryStatus,
  GETQuotesForEnquiry,
  AdminCreateAmcLead,
};
